/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react'
import { useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { dragObjectPositionState, dragObjectShiftState } from '../recoil/atoms'
import { TPage, TSignatureField } from '../types/appTypes'

type TPageItemProps = {
  page: TPage
}

const PageItem = (props: TPageItemProps) => {
  const [currentPage, setCurrentPage] = useState<TPage>(props.page)

  const shift = useRecoilValue(dragObjectShiftState)
  const setPosition = useSetRecoilState(dragObjectPositionState)

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    // Determine the element (img) the signature field has been drop on
    const target = e.target as HTMLDivElement
    // Determine the parent element (div representing the document page) of the image,
    // so that the signature can be attached as an element
    const parent = target.parentElement

    // Retrieve data provided by dragging
    const field = JSON.parse(e.dataTransfer.getData('text/plain')) as TSignatureField

    // Determin the element (div representing the signature field) which was dropped on the target
    const signatureField = document.getElementById(field.taskId as string)

    if (parent && signatureField) {
      // Get the bounding rectangles for accurate positioning
      const pageRect = parent.getBoundingClientRect()

      // Get original page dimensions from data attributes
      const originalWidth = parseFloat(parent.dataset.pagewidth || '0')
      const originalHeight = parseFloat(parent.dataset.pageheight || '0')

      // Calculate scale factor (displayed size vs original size)
      const displayedWidth = pageRect.width
      const scaleFactor = displayedWidth / originalWidth

      // Calculate the drop position relative to the page element (in displayed coordinates)
      const displayedLeft = e.clientX - pageRect.left - shift.shiftX * scaleFactor
      const displayedTop = e.clientY - pageRect.top - shift.shiftY * scaleFactor

      // Convert to original document coordinates for storage
      const originalLeft = displayedLeft / scaleFactor
      const originalTop = displayedTop / scaleFactor

      // Attach signature field to page
      parent.appendChild(signatureField)

      // Set the position in displayed coordinates (scaled)
      signatureField.style.position = 'absolute'
      signatureField.style.top = `${displayedTop}px`
      signatureField.style.left = `${displayedLeft}px`

      // Scale the signature field size
      signatureField.style.transform = `scale(${scaleFactor})`
      signatureField.style.transformOrigin = 'top left'

      // Store the original (unscaled) coordinates for the API
      setPosition({
        top: originalTop,
        left: originalLeft,
      })
    }
  }

  useEffect(() => {
    if (props && props.page) setCurrentPage(props.page)
  }, [props])

  return (
    <div
      id={`${currentPage.documentId}_${currentPage.pageNo}`}
      data-page={currentPage.pageNo}
      data-document-id={currentPage.documentId}
      data-pagewidth={currentPage.width}
      data-pageheight={currentPage.height}
      className="relative mb-6 border border-gray-300 shadow-lg bg-white w-full md:w-auto"
      style={{ maxWidth: currentPage.width, aspectRatio: `${currentPage.width} / ${currentPage.height}` }}
    >
      <img
        className="w-full h-auto"
        src={currentPage.src}
        alt={currentPage.label}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      />
    </div>
  )
}

export default PageItem
